// API Service Layer for Crest Theater
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api`;
  }

  // Generic API call method
  async apiCall(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const finalOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      console.log(`🌐 API Call: ${options.method || 'GET'} ${url}`);
      const response = await fetch(url, finalOptions);
      
      console.log(`📡 Response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error Response: ${errorText}`);
        throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // Handle empty responses
      const text = await response.text();
      const result = text ? JSON.parse(text) : null;
      console.log('✅ API Response success:', result);
      return result;
    } catch (error) {
      console.error(`❌ API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  // EVENTS API
  async getEvents() {
    return await this.apiCall('/events');
  }

  async createEvent(eventData) {
    return await this.apiCall('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  async updateEvent(eventId, eventData) {
    return await this.apiCall(`/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
  }

  async deleteEvent(eventId) {
    try {
      console.log('🗑️ Deleting event:', eventId);
      const result = await this.apiCall(`/events/${eventId}`, {
        method: 'DELETE',
      });
      console.log('✅ Event deletion API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Event deletion API error:', error);
      throw error;
    }
  }

  // CONTACTS API
  async getContacts() {
    return await this.apiCall('/contacts');
  }

  async updateContacts(contactData) {
    return await this.apiCall('/contacts', {
      method: 'PUT',
      body: JSON.stringify(contactData),
    });
  }

  // FEATURED EVENT SELECTION API
  async getFeaturedEventSelection() {
    try {
      return await this.apiCall('/featured-event-selection');
    } catch (error) {
      console.warn('Featured event selection API not available, using localStorage fallback');
      return null;
    }
  }

  async updateFeaturedEventSelection(eventId) {
    try {
      console.log('🔄 Updating featured event selection:', eventId);
      const result = await this.apiCall('/featured-event-selection', {
        method: 'PUT',
        body: JSON.stringify({ selected_event_id: eventId }),
      });
      console.log('✅ Featured event selection API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Featured event selection API error:', error);
      console.warn('Featured event selection API not available, using localStorage fallback');
      return null;
    }
  }

  // FEATURED EVENT API (legacy - keeping for compatibility)
  async getFeaturedEvent() {
    return await this.apiCall('/featured-event');
  }

  async updateFeaturedEvent(featuredEventData) {
    return await this.apiCall('/featured-event', {
      method: 'PUT',
      body: JSON.stringify(featuredEventData),
    });
  }

  // GALLERY API
  async getGalleryImages() {
    try {
      const galleryData = await this.apiCall('/gallery');
      return galleryData ? galleryData.images : [];
    } catch (error) {
      console.warn('Gallery API not available yet, using localStorage fallback');
      return null;
    }
  }

  async updateGalleryImages(galleryImages) {
    try {
      // Ensure galleryImages is an array and validate
      const imagesArray = Array.isArray(galleryImages) ? galleryImages : [];
      const cleanedImages = imagesArray
        .filter(img => typeof img === 'string' && img.trim().length > 0)
        .map(img => img.trim());
      
      console.log('🔄 Updating gallery images:', cleanedImages.length, 'valid images');
      console.log('🔄 Original input type:', typeof galleryImages, 'Array?', Array.isArray(galleryImages));
      const requestBody = { images: cleanedImages };
      console.log('📤 Request body:', JSON.stringify(requestBody));
      
      const galleryData = await this.apiCall('/gallery', {
        method: 'PUT',
        body: JSON.stringify(requestBody),
      });
      
      console.log('✅ Gallery API response:', galleryData);
      return galleryData ? galleryData.images : cleanedImages;
    } catch (error) {
      console.error('❌ Gallery API error:', error);
      console.warn('Gallery API not available yet, using localStorage fallback');
      return null;
    }
  }

  // BANNER SETTINGS API
  async getBannerSettings() {
    try {
      return await this.apiCall('/banner-settings');
    } catch (error) {
      console.warn('Banner settings API not available yet, using localStorage fallback');
      return null;
    }
  }

  async updateBannerSettings(bannerData) {
    try {
      console.log('🔄 Updating banner settings:', bannerData);
      const result = await this.apiCall('/banner-settings', {
        method: 'PUT',
        body: JSON.stringify(bannerData),
      });
      console.log('✅ Banner settings API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Banner settings API error:', error);
      console.warn('Banner settings API not available yet, using localStorage fallback');
      return null;
    }
  }

  // PAGE IMAGES API
  async getPageImages() {
    try {
      return await this.apiCall('/page-images');
    } catch (error) {
      console.warn('Page images API not available yet, using localStorage fallback');
      return null;
    }
  }

  async updatePageImages(pageImagesData) {
    try {
      console.log('🔄 Updating page images:', pageImagesData);
      const result = await this.apiCall('/page-images', {
        method: 'PUT',
        body: JSON.stringify(pageImagesData),
      });
      console.log('✅ Page images API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Page images API error:', error);
      console.warn('Page images API not available yet, using localStorage fallback');
      return null;
    }
  }

  // EMPRESS SETTINGS API
  async getEmpressSettings() {
    try {
      return await this.apiCall('/empress-settings');
    } catch (error) {
      console.warn('Empress settings API not available yet, using localStorage fallback');
      return null;
    }
  }

  async updateEmpressSettings(empressData) {
    try {
      console.log('🔄 Updating empress settings:', empressData);
      const result = await this.apiCall('/empress-settings', {
        method: 'PUT',
        body: JSON.stringify(empressData),
      });
      console.log('✅ Empress settings API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Empress settings API error:', error);
      console.warn('Empress settings API not available yet, using localStorage fallback');
      return null;
    }
  }
}

// Export a singleton instance
const apiService = new ApiService();
export default apiService;